// src/pages/Dashboard.jsx

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../store/authStore";
import axios from "axios";
import "./Dashboard.css";

const API_URL = process.env.REACT_APP_BASE_URL || "http://localhost:3337";

const categoryOptions = [
  "Matériels de lutte contre l'incendie",
  "Équipements pétroliers",
  "Robinetterie et instrumentation",
  "Équipements de salle de lavage",
  "Équipements hydrauliques",
  "Tuyauterie et accessoires de raccordement",
];

const subCategoryOptions = {
  "Matériels de lutte contre l'incendie": [
    "Toutes",
    "Extincteurs",
    "Lances Monitor",
    "Équipements pour sapeurs-pompiers",
    "Équipements pour installations fixes",
  ],
  "Équipements pétroliers": ["Toutes", "petrolier1", "petrolier2", "petrolier3", "petrolier4"],
  "Robinetterie et instrumentation": ["Toutes", "robinet1", "robinet2", "robinet3", "robinet4"],
  "Équipements de salle de lavage": ["Toutes", "lavage1", "lavage2", "lavage3", "lavage4"],
  "Équipements hydrauliques": ["Toutes", "hydraulique1", "hydraulique2", "hydraulique3", "hydraulique4"],
  "Tuyauterie et accessoires de raccordement": ["Toutes", "tuyauterie1", "tuyauterie2", "tuyauterie3", "tuyauterie4"],
};

const extinguisherOptions = ["Toutes", "CO₂", "Poudre", "Eau pulvérisée", "Eau + additif (mousse)", "Accessoires"];
const sapeursOptions = [
  "Toutes",
  "Lance d'incendie",
  "Équipement mousse",
  "Pièces de jonction",
  "Appareils de mesure",
  "Flexible d'incendie et accessoires",
  "Clé de manœuvre",
  "Raccord d'incendie",
];
const installationsOptions = [
  "Toutes",
  "Robinet d'incendie armé (RIA)",
  "Poste d'incendie additif (PIA)",
  "Poteaux et bouches d'incendie",
  "Robinet pour colonnes sèches",
];

const firstSubSubByCategory = {
  "Équipements pétroliers": "petro1",
  "Robinetterie et instrumentation": "tube1",
  "Équipements de salle de lavage": "lav1",
  "Équipements hydrauliques": "hydr1",
  "Tuyauterie et accessoires de raccordement": "rac1",
};

const getFirstRealSubCat = (cat) =>
  (subCategoryOptions[cat] || []).find((x) => x !== "Toutes") || "";

const getDefaultSubSub = (cat) => firstSubSubByCategory[cat] || "";

const getCategoryEndpoint = (category) => {
  const map = {
    "Matériels de lutte contre l'incendie": "/api/incendie",
    "Équipements pétroliers": "/api/petrolier",
    "Robinetterie et instrumentation": "/api/robinetterie",
    "Équipements de salle de lavage": "/api/lavage",
    "Équipements hydrauliques": "/api/hydraulique",
    "Tuyauterie et accessoires de raccordement": "/api/tuyauterie",
  };
  return map[category] || "/api/incendie";
};

export default function Dashboard() {
  const { isAuthenticated, role, token } = useAuthStore();
  const navigate = useNavigate();
  const isAdmin = role === "admin";

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState({});

  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [deletingProductId, setDeletingProductId] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [productForm, setProductForm] = useState({
    name: "",
    description: "",
    category: categoryOptions[0],
    subCategory: "",
    subSubCategory: "",
    imageFile: null,
    pdfFile: null,
    specifications: "{}",
  });
  const [productMessage, setProductMessage] = useState("");

  const [activeTab, setActiveTab] = useState("products");

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) navigate("/");
  }, [isAuthenticated, isAdmin, navigate]);

  /* ── Fetch Users ── */
  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(data);
    } catch (err) {
      console.error("Erreur chargement utilisateurs :", err);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (isAdmin) fetchUsers();
  }, [isAdmin]);

  /* ── Fetch Products ── */
  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const endpoints = ["incendie", "petrolier", "robinetterie", "lavage", "hydraulique", "tuyauterie"];
      const results = await Promise.all(
        endpoints.map((ep) =>
          axios
            .get(`${API_URL}/api/${ep}`, { headers: { Authorization: `Bearer ${token}` } })
            .then((res) => res.data)
            .catch(() => [])
        )
      );
      setProducts(results.flat());
    } catch (err) {
      console.error("Erreur chargement produits :", err);
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    if (isAdmin) fetchProducts();
  }, [isAdmin]);

  /* ── Role Management ── */
  const handleRoleSelect = (userId, newRole) =>
    setSelectedRoles((prev) => ({ ...prev, [userId]: newRole }));

  const handleRoleChange = async (userId) => {
    const newRole = selectedRoles[userId];
    if (!newRole) return;
    try {
      await axios.put(
        `${API_URL}/api/users/${userId}`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers((u) => u.map((x) => (x._id === userId ? { ...x, role: newRole } : x)));
      setSelectedRoles((prev) => ({ ...prev, [userId]: undefined }));
    } catch (err) {
      console.error("Erreur mise à jour rôle :", err);
    }
  };

  /* ── Product Form ── */
  const isIncendie = productForm.category === "Matériels de lutte contre l'incendie";

  const handleProductChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "imageFile" || name === "pdfFile") {
      setProductForm((prev) => ({ ...prev, [name]: files[0] || null }));
      return;
    }

    if (name === "category") {
      const nextIsIncendie = value === "Matériels de lutte contre l'incendie";
      setProductForm((prev) => ({
        ...prev,
        category: value,
        subCategory: nextIsIncendie ? "" : getFirstRealSubCat(value),
        subSubCategory: nextIsIncendie ? "" : getDefaultSubSub(value),
      }));
      return;
    }

    if (name === "subCategory") {
      setProductForm((prev) => ({
        ...prev,
        subCategory: value,
        subSubCategory: isIncendie ? prev.subSubCategory : "",
      }));
      return;
    }

    setProductForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    setProductMessage("");

    const { category } = productForm;

    if (category === "Matériels de lutte contre l'incendie") {
      const { subCategory, subSubCategory } = productForm;
      if (!subCategory) {
        setProductMessage("Veuillez choisir une sous-catégorie pour Incendie.");
        return;
      }
      if (
        ["Extincteurs", "Équipements pour sapeurs-pompiers", "Équipements pour installations fixes"].includes(subCategory) &&
        !subSubCategory
      ) {
        setProductMessage("Veuillez renseigner la sous-sous-catégorie (Type).");
        return;
      }
    }

    const effectiveSubCategory = isIncendie
      ? productForm.subCategory
      : productForm.subCategory || getFirstRealSubCat(category);

    const effectiveSubSubCategory = isIncendie
      ? productForm.subSubCategory
      : productForm.subSubCategory || getDefaultSubSub(category);

    try {
      const formData = new FormData();
      formData.append("name", productForm.name);
      formData.append("description", productForm.description);
      formData.append("category", category);
      formData.append("subCategory", effectiveSubCategory || "");
      if (effectiveSubSubCategory) formData.append("subSubCategory", effectiveSubSubCategory);
      if (productForm.imageFile) formData.append("image", productForm.imageFile);
      if (productForm.pdfFile) formData.append("pdf", productForm.pdfFile);
      formData.append("specifications", productForm.specifications);

      const endpoint = getCategoryEndpoint(category);

      await axios.post(`${API_URL}${endpoint}`, formData, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
      });

      setProductMessage("Produit ajouté avec succès ✅");
      setProductForm({
        name: "",
        description: "",
        category: categoryOptions[0],
        subCategory: "",
        subSubCategory: "",
        imageFile: null,
        pdfFile: null,
        specifications: "{}",
      });
      fetchProducts();
    } catch (err) {
      console.error("Erreur ajout produit :", err);
      setProductMessage("Échec de l'ajout du produit ❌");
    }
  };

  const handleDeleteProduct = async (productId, category) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce produit ?")) return;

    try {
      setDeletingProductId(productId);
      const endpoint = getCategoryEndpoint(category);
      await axios.delete(`${API_URL}${endpoint}/${productId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts((prev) => prev.filter((p) => p._id !== productId));
    } catch (err) {
      console.error("Erreur suppression produit :", err);
      alert("Une erreur est survenue lors de la suppression.");
    } finally {
      setDeletingProductId(null);
    }
  };

  const currentIncendieSubSub = (() => {
    const { subCategory } = productForm;
    if (!isIncendie) return [];
    if (subCategory === "Extincteurs") return extinguisherOptions;
    if (subCategory === "Équipements pour sapeurs-pompiers") return sapeursOptions;
    if (subCategory === "Équipements pour installations fixes") return installationsOptions;
    return [];
  })();

  const autoSubCatForInfo = !isIncendie ? productForm.subCategory || getFirstRealSubCat(productForm.category) : "";
  const autoSubSubForInfo = !isIncendie ? productForm.subSubCategory || getDefaultSubSub(productForm.category) : "";

  return (
    <div className="dash">
      {/* ── Header ── */}
      <div className="dash__header">
        <div className="dash__header-inner">
          <div className="dash__title-group">
            <h1 className="dash__title">
              <i className="fa-solid fa-gauge-high"></i> Dashboard
            </h1>
            <p className="dash__subtitle">Gérez vos produits et utilisateurs</p>
          </div>
          <div className="dash__stats">
            <div className="dash__stat">
              <span className="dash__stat-num">{products.length}</span>
              <span className="dash__stat-label">Produits</span>
            </div>
            <div className="dash__stat">
              <span className="dash__stat-num">{users.length}</span>
              <span className="dash__stat-label">Utilisateurs</span>
            </div>
          </div>
        </div>
      </div>

      <div className="dash__container">
        {/* ── Tabs ── */}
        <div className="dash__tabs">
          <button
            className={`dash__tab ${activeTab === "products" ? "dash__tab--active" : ""}`}
            onClick={() => setActiveTab("products")}
          >
            <i className="fa-solid fa-box"></i> Produits
          </button>
          <button
            className={`dash__tab ${activeTab === "users" ? "dash__tab--active" : ""}`}
            onClick={() => setActiveTab("users")}
          >
            <i className="fa-solid fa-users"></i> Utilisateurs
          </button>
        </div>

        {/* ═════ Products Tab ═════ */}
        {activeTab === "products" && (
          <div className="dash__section">
            {/* Toolbar */}
            <div className="dash__toolbar">
              <h2 className="dash__section-title">
                <i className="fa-solid fa-box"></i> Gestion des produits
              </h2>
              <div className="dash__toolbar-actions">
                <button className="dash__btn dash__btn--outline" onClick={fetchProducts}>
                  <i className="fa-solid fa-arrows-rotate"></i> Actualiser
                </button>
                {isAdmin && (
                  <button
                    className={`dash__btn ${showForm ? "dash__btn--danger" : "dash__btn--primary"}`}
                    onClick={() => {
                      setShowForm((f) => !f);
                      setProductMessage("");
                    }}
                  >
                    {showForm ? (
                      <><i className="fa-solid fa-xmark"></i> Fermer</>
                    ) : (
                      <><i className="fa-solid fa-plus"></i> Ajouter</>
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* ── Add Form ── */}
            {isAdmin && showForm && (
              <div className="dash__form-card">
                <h3 className="dash__form-title">
                  <i className="fa-solid fa-circle-plus"></i> Nouveau produit
                </h3>
                {productMessage && (
                  <div className={`dash__alert ${productMessage.includes("✅") ? "dash__alert--success" : "dash__alert--error"}`}>
                    {productMessage}
                  </div>
                )}

                <form onSubmit={handleProductSubmit} encType="multipart/form-data" className="dash__form">
                  <div className="dash__form-grid">
                    <div className="dash__field">
                      <label className="dash__label">Nom *</label>
                      <input
                        type="text"
                        name="name"
                        className="dash__input"
                        value={productForm.name}
                        onChange={handleProductChange}
                        required
                        placeholder="Nom du produit"
                      />
                    </div>

                    <div className="dash__field">
                      <label className="dash__label">Catégorie *</label>
                      <select
                        name="category"
                        className="dash__select"
                        value={productForm.category}
                        onChange={handleProductChange}
                      >
                        {categoryOptions.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="dash__field">
                    <label className="dash__label">Description</label>
                    <textarea
                      name="description"
                      className="dash__textarea"
                      rows={2}
                      value={productForm.description}
                      onChange={handleProductChange}
                      placeholder="Description du produit"
                    />
                  </div>

                  {/* Incendie sub-categories */}
                  {isIncendie ? (
                    <div className="dash__form-grid">
                      <div className="dash__field">
                        <label className="dash__label">Sous-catégorie *</label>
                        <select
                          name="subCategory"
                          className="dash__select"
                          value={productForm.subCategory}
                          onChange={handleProductChange}
                          required
                        >
                          <option value="">-- Choisir --</option>
                          {(subCategoryOptions[productForm.category] || []).map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>

                      {["Extincteurs", "Équipements pour sapeurs-pompiers", "Équipements pour installations fixes"].includes(
                        productForm.subCategory
                      ) && (
                        <div className="dash__field">
                          <label className="dash__label">
                            {productForm.subCategory === "Extincteurs"
                              ? "Type d'Extincteur"
                              : productForm.subCategory === "Équipements pour sapeurs-pompiers"
                              ? "Type Sapeurs-Pompiers"
                              : "Type Installations fixes"}
                          </label>
                          <select
                            name="subSubCategory"
                            className="dash__select"
                            value={productForm.subSubCategory}
                            onChange={handleProductChange}
                            required
                          >
                            <option value="">-- Choisir --</option>
                            {currentIncendieSubSub.map((opt) => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="dash__auto-info">
                      <i className="fa-solid fa-circle-info"></i>
                      Sous-catégorie et type définis automatiquement :{" "}
                      <strong>{autoSubCatForInfo || "—"} / {autoSubSubForInfo || "—"}</strong>
                    </div>
                  )}

                  <div className="dash__form-grid">
                    <div className="dash__field">
                      <label className="dash__label">Image</label>
                      <input
                        type="file"
                        name="imageFile"
                        className="dash__file"
                        accept="image/*"
                        onChange={handleProductChange}
                      />
                    </div>
                    <div className="dash__field">
                      <label className="dash__label">PDF</label>
                      <input
                        type="file"
                        name="pdfFile"
                        className="dash__file"
                        accept="application/pdf"
                        onChange={handleProductChange}
                      />
                    </div>
                  </div>

                  <div className="dash__field">
                    <label className="dash__label">Spécifications (JSON)</label>
                    <textarea
                      name="specifications"
                      className="dash__textarea"
                      rows={3}
                      value={productForm.specifications}
                      onChange={handleProductChange}
                      placeholder='{ "PRODUCTCODE": "XYZ-123", "poids": "6kg" }'
                    />
                  </div>

                  <button type="submit" className="dash__btn dash__btn--primary dash__btn--lg">
                    <i className="fa-solid fa-check"></i> Ajouter le produit
                  </button>
                </form>
              </div>
            )}

            {/* ── Products Table ── */}
            <div className="dash__card">
              {loadingProducts ? (
                <div className="dash__loading">
                  <div className="dash__spinner"></div>
                  <span>Chargement des produits...</span>
                </div>
              ) : (
                <div className="dash__table-wrap">
                  <table className="dash__table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Nom</th>
                        <th>Catégorie</th>
                        <th>Sous-catégorie</th>
                        <th>Type</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.length > 0 ? (
                        products.map((p, idx) => (
                          <tr key={p._id}>
                            <td>{idx + 1}</td>
                            <td className="dash__td-name">{p.name}</td>
                            <td><span className="dash__badge">{p.category}</span></td>
                            <td>{p.subCategory || "—"}</td>
                            <td>{p.subSubCategory || "—"}</td>
                            <td>
                              <button
                                className="dash__btn-icon dash__btn-icon--danger"
                                onClick={() => handleDeleteProduct(p._id, p.category)}
                                disabled={deletingProductId === p._id}
                                title="Supprimer"
                              >
                                {deletingProductId === p._id ? (
                                  <div className="dash__spinner dash__spinner--sm"></div>
                                ) : (
                                  <i className="fa-solid fa-trash"></i>
                                )}
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={6} className="dash__empty">
                            Aucun produit trouvé.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═════ Users Tab ═════ */}
        {activeTab === "users" && (
          <div className="dash__section">
            <div className="dash__toolbar">
              <h2 className="dash__section-title">
                <i className="fa-solid fa-users"></i> Gestion des utilisateurs
              </h2>
              <button className="dash__btn dash__btn--outline" onClick={fetchUsers}>
                <i className="fa-solid fa-arrows-rotate"></i> Actualiser
              </button>
            </div>

            <div className="dash__card">
              {loadingUsers ? (
                <div className="dash__loading">
                  <div className="dash__spinner"></div>
                  <span>Chargement des utilisateurs...</span>
                </div>
              ) : (
                <div className="dash__table-wrap">
                  <table className="dash__table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Email</th>
                        <th>Rôle</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.length > 0 ? (
                        users.map((u, i) => (
                          <tr key={u._id}>
                            <td>{i + 1}</td>
                            <td>{u.email}</td>
                            <td>
                              <div className="dash__role-cell">
                                <select
                                  className="dash__select dash__select--sm"
                                  value={selectedRoles[u._id] ?? u.role}
                                  onChange={(e) => handleRoleSelect(u._id, e.target.value)}
                                >
                                  <option value="admin">Admin</option>
                                  <option value="moderator">Moderator</option>
                                  <option value="user">User</option>
                                </select>
                                {selectedRoles[u._id] !== undefined && selectedRoles[u._id] !== u.role && (
                                  <button
                                    className="dash__btn-icon dash__btn-icon--success"
                                    onClick={() => handleRoleChange(u._id)}
                                    title="Confirmer"
                                  >
                                    <i className="fa-solid fa-check"></i>
                                  </button>
                                )}
                              </div>
                            </td>
                            <td>
                              <button
                                className="dash__btn-icon dash__btn-icon--danger"
                                onClick={() => console.log("Suppression futur")}
                                title="Supprimer"
                              >
                                <i className="fa-solid fa-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4} className="dash__empty">
                            Aucun utilisateur trouvé.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
